import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { recordingsAPI, fichiersAPI } from '../services/api';
import API_URL from '../services/api';
import './Profile.css';

import { Warehouse, UserRoundPen, CalendarDays, School, Shield, Lock, Video, FolderOpen, Menu, X, User, Settings, Eye, EyeOff, Timer, SquarePlay, Trash2 } from 'lucide-react';

// Get the base API URL without /api
const getBaseUrl = () => {
  return API_URL.replace('/api', '');
};

const Profile = () => {
  const { user, updateProfile, changePassword, setup2FA, enable2FA, disable2FA, logout } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('profile');
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [avatar, setAvatar] = useState(user?.avatar || '');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

   // Password change state
   const [currentPassword, setCurrentPassword] = useState('');
   const [newPassword, setNewPassword] = useState('');
   const [confirmPassword, setConfirmPassword] = useState('');
   const [showCurrentPassword, setShowCurrentPassword] = useState(false);
   const [showNewPassword, setShowNewPassword] = useState(false);
   const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // 2FA state
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');

  // Recordings state
  const [recordings, setRecordings] = useState([]);
  const [recordingsLoading, setRecordingsLoading] = useState(false);
  const [playingRecording, setPlayingRecording] = useState(null);
  const [sharingRecordingId, setSharingRecordingId] = useState(null); // Pour désactiver le bouton pendant envoi

  // Files state
  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);

  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    closeMobileMenu();
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (!/^[a-zA-ZÀ-ÖØ-öø-ÿ\s'-]+$/.test(name)) {
      setError('Le nom ne peut contenir que des lettres, espaces, apostrophes et traits d\'union');
      setLoading(false);
      return;
    }

    if (name.length < 2 || name.length > 50) {
      setError('Le nom doit contenir entre 2 et 50 caractères');
      setLoading(false);
      return;
    }

    try {
      await updateProfile({ name, email, avatar });
      setSuccess('Profil mis à jour avec succès');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('Les nouveaux mots de passe ne correspondent pas');
      return;
    }

    if (newPassword.length < 6) {
      setError('Le mot de passe doit comporter au moins 6 caractères.');
      return;
    }

    setLoading(true);
    try {
      await changePassword(currentPassword, newPassword);
      setSuccess('Mot de passe modifié avec succès');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSetup2FA = async () => {
    setError('');
    setLoading(true);
    try {
      const result = await setup2FA();
      if (result.success) {
        setQrCodeUrl('sent');
        setSuccess('Un code de vérification a été envoyé à votre adresse email');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEnable2FA = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await enable2FA(twoFactorCode);
      setSuccess('2FA activé avec succès');
      setQrCodeUrl('');
      setTwoFactorCode('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    const password = window.prompt('Enter your password to disable 2FA:');
    if (!password) return;

    setError('');
    setLoading(true);
    try {
      await disable2FA(password);
      setSuccess('2FA disabled successfully');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Fetch user recordings
  useEffect(() => {
    if (activeTab === 'recordings' && user?.id) {
      fetchUserRecordings();
    }
  }, [activeTab, user]);

  const fetchUserRecordings = async () => {
    setRecordingsLoading(true);
    try {
      const response = await recordingsAPI.getUserRecordings(user.id);
      if (response.success) {
        setRecordings(response.data);
      }
    } catch (err) {
      console.error('Error fetching recordings:', err);
    } finally {
      setRecordingsLoading(false);
    }
  };

   const handleDeleteRecording = async (recordingId) => {
     if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet enregistrement ?')) {
       return;
     }
     try {
       await recordingsAPI.deleteRecording(recordingId);
       setRecordings(recordings.filter(r => r.id !== recordingId));
     } catch (err) {
       console.error('Erreur lors de la suppression de l\'enregistrement:', err);
     }
   };

   const handleShareRecording = async (recordingId) => {
     setError('');
     setSuccess('');
     setSharingRecordingId(recordingId);
     try {
       const response = await recordingsAPI.shareRecording(recordingId, {
         userId: user.id
       });
       if (response.success) {
         setSuccess(`Enregistrement partagé avec ${response.recipientsCount} participants !`);
       }
     } catch (err) {
       setError(err.message || 'Erreur lors du partage');
     } finally {
       setSharingRecordingId(null);
     }
   };

  const fetchUserFiles = async () => {
    if (!user?.id) return;
    setFilesLoading(true);
    try {
      const response = await fichiersAPI.getUserFiles(user.id);
      if (response.success) {
        setFiles(response.data);
      }
    } catch (err) {
      console.error('Error fetching files:', err);
    } finally {
      setFilesLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 o';
    if (bytes < 1024) return bytes + ' o';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' Ko';
    return (bytes / (1024 * 1024)).toFixed(1) + ' Mo';
  };

  const formatDuration = (duration) => {
    if (!duration) return '00:00:00';
    return duration;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!user) {
    return <div className="profile-loading">Veuillez vous connecter pour consulter votre profil.</div>;
  }

  return (
    <div className="profile-page">
      <button className="mobile-menu-toggle" onClick={toggleMobileMenu}>
        {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
      </button>
      
      <div 
        className={`sidebar-overlay ${mobileMenuOpen ? 'visible' : ''}`} 
        onClick={closeMobileMenu}
      />
      
      <div className={`profile-sidebar ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h2>Tableau de bord</h2>
          <p>{user?.name || 'Mon Compte'}</p>
        </div>
        <div className="sidebar-menu">
          <div className="sidebar-section-title">Navigation</div>
          <Link to="/salle" className="sidebar-item" onClick={closeMobileMenu}>
            <span className="sidebar-icon"><Warehouse size={20} /></span>
            <span>Mes salles</span>
          </Link>
          {/*
          <Link to="/meetings" className="sidebar-item" onClick={closeMobileMenu}>
            <span className="sidebar-icon"><School size={20} /></span>
            <span>Mes réunions</span>
          </Link>
          */}
          {/*
          <Link to="/schedule" className="sidebar-item" onClick={closeMobileMenu}>
            <span className="sidebar-icon"><CalendarDays size={20} /></span>
            <span>Réunions planifiées</span>
          </Link>
          */}
          
          <div className="sidebar-divider" />
          <div className="sidebar-section-title">Mon Compte</div>
          
          <div 
            className={`sidebar-item ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => handleTabChange('profile')}
          >
            <span className="sidebar-icon"><User size={20} /></span>
            <span>Profil</span>
          </div>
          <div 
            className={`sidebar-item ${activeTab === 'security' ? 'active' : ''}`}
            onClick={() => handleTabChange('security')}
          >
            <span className="sidebar-icon"><Shield size={20} /></span>
            <span>Sécurité</span>
          </div>
          <div 
            className={`sidebar-item ${activeTab === 'password' ? 'active' : ''}`}
            onClick={() => handleTabChange('password')}
          >
            <span className="sidebar-icon"><Lock size={20} /></span>
            <span>Mot de passe</span>
          </div>
          <div 
            className={`sidebar-item ${activeTab === 'recordings' ? 'active' : ''}`}
            onClick={() => handleTabChange('recordings')}
          >
            <span className="sidebar-icon"><Video size={20} /></span>
            <span>Enregistrement</span>
          </div>
          <div 
            className={`sidebar-item ${activeTab === 'files' ? 'active' : ''}`}
            onClick={() => {
              handleTabChange('files');
              if (files.length === 0) {
                fetchUserFiles();
              }
            }}
          >
            <span className="sidebar-icon"><FolderOpen size={20} /></span>
            <span>Fichiers</span>
          </div>
        </div>
      </div>

      <div className="profile-main">
      <div className="profile-header">
        <h1>Mon Profil</h1>
        <p>Gérer vos paramètres de compte</p>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="profile-content">
        {activeTab === 'profile' && (
          <form onSubmit={handleProfileUpdate} className="profile-form">
            <div className="profile-picture-section">
              <div className="profile-picture-container">
                {avatar ? (
                  <img src={avatar} alt="Profile" className="profile-picture" />
                ) : (
                  <div className="profile-picture-placeholder">
                    {name ? name.charAt(0).toUpperCase() : '?'}
                  </div>
                )}
              </div>
              <div className="profile-picture-info">
                <h3>{name || 'Your Name'}</h3>
                <p>{email}</p>
              </div>
            </div>

            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled
              />
              <small>L'email ne peut pas être modifié</small>
            </div>

            <div className="form-group">
              <label>Tout le nom</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            
            {/*
            <div className="form-group">
              <label>Avatar URL</label>
              <input
                type="url"
                value={avatar}
                onChange={(e) => setAvatar(e.target.value)}
                placeholder="https://example.com/avatar.jpg"
              />
            </div>
            */}
            {/*
            <div className="form-group">
              <label>Role</label>
              <input type="text" value={user.role} disabled />
            </div>
            */}
            {/*
            <div className="form-group">
              <label>Membre depuis</label>
              <input 
                type="text" 
                value={new Date(user.createdAt).toLocaleDateString()} 
                disabled 
              />
            </div>*/}

            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Enregistrement...' : 'Enregistrer les modifications'}
            </button>
          </form>
        )}

        {activeTab === 'security' && (
          <div className="security-section">
            <h2>Authentification à deux facteurs</h2>
            <p>Ajouter une couche supplémentaire de sécurité à votre compte</p>

            {user.twoFactorEnabled ? (
              <div className="two-factor-enabled">
                <div className="status-indicator">
                  <span className="status-dot active"></span>
                  <span>2FA est activée</span>
                </div>
                <button 
                  className="btn-danger" 
                  onClick={handleDisable2FA}
                  disabled={loading}
                >
                  Désactiver 2FA
                </button>
              </div>
            ) : (
              <div className="two-factor-setup">
                {!qrCodeUrl ? (
                  <button 
                    className="btn-primary" 
                    onClick={handleSetup2FA}
                    disabled={loading}
                  >
                    {loading ? 'Activation...' : 'Activer 2FA'}
                  </button>
                ) : (
                  <div className="qr-setup">
                    <p>Un code de vérification a été envoyé à votre adresse email. Veuillez le saisir ci-dessous :</p>

                    <form onSubmit={handleEnable2FA}>
                      <div className="form-group">
                        <label>Entrez le code de vérification</label>
                        <input
                          type="text"
                          value={twoFactorCode}
                          onChange={(e) => setTwoFactorCode(e.target.value)}
                          placeholder="123456"
                          required
                          autoComplete="one-time-code"
                          maxLength={6}
                          inputMode="numeric"
                          pattern="[0-9]*"
                        />
                      </div>
                      <button type="submit" className="btn-primary" disabled={loading}>
                        {loading ? 'Vérification...' : 'Vérifier et activer 2FA'}
                      </button>
                    </form>
                  </div>
                )}
              </div>
            )}

            <div className="logout-section">
              <h2>Se déconnecter</h2>
              <p>Déconnectez-vous de votre compte sur cet appareil</p>
              <button className="btn-secondary" onClick={handleLogout}>
                Se déconnecter
              </button>
            </div>
          </div>
        )}

        {activeTab === 'password' && (
          <form onSubmit={handlePasswordChange} className="password-form">
             <div className="form-group">
               <label>Mot de passe actuel</label>
               <div className="password-input-wrapper">
                 <input
                   type={showCurrentPassword ? 'text' : 'password'}
                   value={currentPassword}
                   onChange={(e) => setCurrentPassword(e.target.value)}
                   required
                 />
                 <button
                   type="button"
                   onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                   aria-label={showCurrentPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                   className="password-toggle"
                 >
                   {showCurrentPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                 </button>
               </div>
             </div>

             <div className="form-group">
               <label>Nouveau mot de passe</label>
               <div className="password-input-wrapper">
                 <input
                   type={showNewPassword ? 'text' : 'password'}
                   value={newPassword}
                   onChange={(e) => setNewPassword(e.target.value)}
                   required
                   minLength={6}
                 />
                 <button
                   type="button"
                   onClick={() => setShowNewPassword(!showNewPassword)}
                   aria-label={showNewPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                   className="password-toggle"
                 >
                   {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                 </button>
               </div>
             </div>

             <div className="form-group">
               <label>Confirmer le nouveau mot de passe</label>
               <div className="password-input-wrapper">
                 <input
                   type={showConfirmPassword ? 'text' : 'password'}
                   value={confirmPassword}
                   onChange={(e) => setConfirmPassword(e.target.value)}
                   required
                 />
                 <button
                   type="button"
                   onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                   aria-label={showConfirmPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                   className="password-toggle"
                 >
                   {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                 </button>
               </div>
             </div>

            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Changement...' : 'Changer mot de passe'}
            </button>
          </form>
        )}

        {activeTab === 'recordings' && (
          <div className="recordings-section">
            <h2>Mes enregistrements</h2>
            <p>Voici la liste de vos enregistrements de réunions</p>

            {recordingsLoading ? (
              <div className="loading">Chargement des enregistrements...</div>
            ) : recordings.length === 0 ? (
              <div className="no-recordings">
                <p>Vous n'avez pas encore d'enregistrements.</p>
                <p>Les enregistrements apparaîtront ici après une réunion.</p>
              </div>
            ) : (
              <div className="recordings-list">
                {recordings.map((recording) => (
                  <div key={recording.id} className="recording-item">
                    <div className="recording-info">
                      <h3>{recording.title || 'Enregistrement sans titre'}</h3>
                      <p className="recording-date">
                        <CalendarDays /> {formatDate(recording.dateEnregist)}
                      </p>
                      <p className="recording-duration">
                        <Timer /> Durée: {formatDuration(recording.duree)}
                      </p>
                      {recording.idMeeting && (
                        <p className="recording-room">
                          <User /> Réunion associée
                        </p>
                      )}
                      {recording.filePath && (
                        <button
                          className="btn-play btn-sm"
                          onClick={() => setPlayingRecording(recording)}
                          title="Lire"
                        >
                          <SquarePlay /> Lire
                        </button>
                      )}
                    </div>
                    <div className="recording-actions">
                      <button
                        className="btn-share btn-sm"
                        onClick={() => handleShareRecording(recording.id)}
                        title={recording.idMeeting ? "Partager avec les participants" : "Partage disponible uniquement pour les enregistrements de réunion"}
                        disabled={!recording.filePath || !recording.idMeeting || sharingRecordingId === recording.id}
                      >
                        {sharingRecordingId === recording.id ? '⏳...' : '📤 Partager'}
                      </button>
                      <button
                        className="btn-danger btn-sm"
                        onClick={() => handleDeleteRecording(recording.id)}
                        title="Supprimer"
                      >
                        <Trash2 /> Supprimer
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'files' && (
          <div className="files-section">
            <h2>Mes fichiers partagés</h2>
            <p>Voici la liste des fichiers que vous avez partagés pendant les réunions</p>

            {filesLoading ? (
              <div className="loading">Chargement des fichiers...</div>
            ) : files.length === 0 ? (
              <div className="no-files">
                <p>Vous n'avez pas encore partagé de fichiers.</p>
                <p>Les fichiers apparaîtront ici après les avoir partagés dans une réunion.</p>
              </div>
            ) : (
              <div className="files-list">
                {files.map((file) => (
                  <div key={file.idfichier} className="file-item">
                    <div className="file-icon">
                      {file.typefichier === 'pdf' ? '📄' : 
                       file.typefichier === 'jpg' || file.typefichier === 'png' || file.typefichier === 'jpeg' ? '🖼️' :
                       file.typefichier === 'doc' || file.typefichier === 'docx' ? '📝' :
                       file.typefichier === 'xls' || file.typefichier === 'xlsx' ? '📊' :
                       file.typefichier === 'zip' || file.typefichier === 'rar' ? '📦' : '📎'}
                    </div>
                    <div className="file-info">
                      <h3>{file.nomfichier || 'Fichier sans nom'}</h3>
                      <p className="file-meta">
                        <span className="file-size">{formatFileSize(file.taille)}</span>
                        <span className="file-date"> - {formatDate(file.date_envoi)}</span>
                      </p>
                    </div>
                    <div className="file-actions">
                      <a 
                        href={`${getBaseUrl()}/api/fichiers/download/${encodeURIComponent(file.url.split('/').pop())}`}
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="btn-primary btn-sm"
                      >
                        Télécharger
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        </div>
      </div>

      {/* Video Player Modal */}
      {playingRecording && (
        <div className="modal-overlay" onClick={() => setPlayingRecording(null)}>
          <div className="video-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Lecture de l'enregistrement</h3>
            <video
              controls
              autoPlay
              src={getBaseUrl() + playingRecording.filePath}
              className="video-player"
            />
            <div className="video-info">
              <p>Date: {formatDate(playingRecording.dateEnregist)}</p>
              <p>Durée: {formatDuration(playingRecording.duree)}</p>
            </div>
            <button
              className="btn-secondary"
              onClick={() => setPlayingRecording(null)}
            >
              Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
